import {createSlice} from '@reduxjs/toolkit'
import PaymentService from '../../api/payment'
import CustomerService from '../../api/customer'
import {toast} from 'react-toastify'
import {routes} from '../../routes'
import data from './../../data/limits.json'
import localStorageKeys from '../../utils/localStorageKeys'
import plansData from '../../data/plans.json'
import statusCodes from '../../utils/http'

const COUNT = 'count'
const enterpriseOtherLimits = [
    {
        key: 'premium-individual-support',
        title: 'Premium Individual Support',
        module: 'other',
        show: true,
        type: 'feature',
        value: 1
    },
    {
        key: 'infrastructure-personalization',
        title: 'Infrastructure-based Personalization',
        module: 'other',
        show: true,
        type: 'feature',
        value: 1
    },
    {
        key: 'incident-management-setup',
        title: 'Incident Management Setup',
        module: 'other',
        show: true,
        type: 'feature',
        value: 1
    },
    {
        key: 'individual-consulting',
        title: 'Individual Consulting',
        module: 'other',
        show: true,
        type: 'feature',
        value: 1
    }
]

const BUSINESS_PLAN = 'business'
const CUSTOMIZED_LIMITS = 'Customizable'
const defaultEnterprisePlan = {
    plan: 'Enterprise',
    plan_id: 'enterprise'
}

export const plansSlice = createSlice({
    name: 'plans',
    initialState: {
        plans: [],
        isFetching: false,
        plansLimits: {}
    },
    reducers: {
        setPlans: (state, {payload}) => {
            state.plans = payload
        },
        setIsFetching: (state, {payload}) => {
            state.isFetching = payload
        },
        setPlansLimits: (state, {payload}) => {
            state.plansLimits = payload
        }
    }
})

export const {setPlans, setIsFetching, setPlansLimits} = plansSlice.actions
export default plansSlice.reducer

export const getPlans = () => {
    return async(dispatch) => {
        dispatch(setIsFetching(true))
        try {
            const {data} = await PaymentService.getPlans()
            dispatch(setPlans(data?.data))
            dispatch(setIsFetching(false))
        } catch (e) {
            toast.error(e?.response.data?.message)
            dispatch(setIsFetching(false))
        }
    }
}

export const getPlanLimits = (navigate, isAppsumoUser) => {
    const currentPlan = localStorage.getItem(localStorageKeys.PLAN)

    return async(dispatch) => {
        dispatch(setIsFetching(true))
        try {
            const organizedPlans = isAppsumoUser
                ? data.plans.flatMap((item) =>
                    item.versions.length === 1
                        ? [item]
                        : item.versions.map((version) => ({name: item.name, versions: [version]}))
                ).filter((item) => {
                    const isAppSumoVersion = item.versions[0].includes(plansData.appsumo_key)
                    if (isAppSumoVersion && (currentPlan.includes(plansData.appsumo_key) || currentPlan !== plansData.free_v2.key)) {
                        return `team#${item.versions[0]}` === currentPlan
                    }
                    return true
                })
                : data.plans

            const res = await Promise.all(organizedPlans.map((plan) => CustomerService.getPlanLimits(plan.name, plan.versions[0])))

            const processLimits = (limits, planKey) =>
                limits?.find((limit) => limit.plan === planKey)?.limits?.map((limit) =>
                    limit?.type === COUNT
                        ? {...limit, value: CUSTOMIZED_LIMITS, value_to_show: CUSTOMIZED_LIMITS}
                        : limit
                )

            const monitoringLimits = res.map((item, index) => ({
                plan: item?.data?.data?.plan,
                plan_id: `${organizedPlans[index].name}#${organizedPlans[index].versions[0]}`,
                limits: item?.data?.data?.modules?.monitoring || []
            }))

            const statuspageLimits = res.map((item, index) => ({
                plan: item?.data?.data?.plan,
                plan_id: `${organizedPlans[index].name}#${organizedPlans[index].versions[0]}`,
                limits: item?.data?.data?.modules?.statuspage || []
            }))

            const incidentManagementLimits = res.map((item, index) => ({
                plan: item?.data?.data?.plan,
                plan_id: `${organizedPlans[index].name}#${organizedPlans[index].versions[0]}`,
                limits: item?.data?.data?.modules?.incident_management || []
            }))

            const enterpriseMonitoringLimits = processLimits(monitoringLimits, BUSINESS_PLAN)
            const enterpriseStatuspageLimits = processLimits(statuspageLimits, BUSINESS_PLAN)
            const enterpriseIncidentManagementLimits = processLimits(incidentManagementLimits, BUSINESS_PLAN)

            monitoringLimits.push({
                plan: defaultEnterprisePlan.plan,
                plan_id: defaultEnterprisePlan.plan_id,
                limits: [...enterpriseMonitoringLimits]
            })

            statuspageLimits.push({
                plan: defaultEnterprisePlan.plan,
                plan_id: defaultEnterprisePlan.plan_id,
                limits: [...enterpriseStatuspageLimits]
            })

            incidentManagementLimits.push({
                plan: defaultEnterprisePlan.plan,
                plan_id: defaultEnterprisePlan.plan_id,
                limits: [...enterpriseIncidentManagementLimits]
            })

            const otherLimits = statuspageLimits.map(plan => ({...plan,
                limits: plan.plan_id !== defaultEnterprisePlan.plan_id ? enterpriseOtherLimits.map(limit => ({...limit, value: 0})) : enterpriseOtherLimits}))

            dispatch(
                setPlansLimits({
                    monitoring: monitoringLimits,
                    statuspage: statuspageLimits,
                    incident_management: incidentManagementLimits,
                    other: otherLimits
                })
            )
            dispatch(setIsFetching(false))
        } catch (e) {
            dispatch(setIsFetching(false))
            toast.error(e?.response?.data?.message)
            if (statusCodes.SpecialErrors.includes(e?.response?.status)) {
                navigate(routes.not_found.path)
            }
        }
    }
}
